import {
  Search, X, ChefHat 
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type { Recipe } from '../../types';
import type { RecipeFormData } from './form-types';
import {
  CATEGORIES, CUISINES, YIELD_UNITS, DIFFICULTY_OPTIONS 
} from './form-constants';

interface BasicInfoTabProps {
  readonly formData: RecipeFormData;
  readonly updateField: <K extends keyof RecipeFormData>(field: K, value: RecipeFormData[K]) => void;
  readonly toggleArrayItem: (field: 'allergens' | 'dietaryFlags' | 'subRecipeIds', value: string) => void;
  readonly allRecipes: Recipe[];
  readonly currentRecipeId?: string;
}

export function BasicInfoTab({
  formData,
  updateField,
  toggleArrayItem,
  allRecipes,
  currentRecipeId,
}: BasicInfoTabProps) {
  const [subRecipeSearch, setSubRecipeSearch] = React.useState('');

  const availableSubRecipes = allRecipes.filter(
    (r) =>
      r.recipeId !== currentRecipeId &&
      (r.type === 'sub-recipe' || r.type === 'component') &&
      r.name.toLowerCase().includes(subRecipeSearch.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Name & Type */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">
            Recipe Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="e.g., Grilled Salmon with Herbs"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">
            Type <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.type}
            onChange={(e) => updateField('type', e.target.value as RecipeFormData['type'])}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="finished">Menu Item (Finished)</option>
            <option value="sub-recipe">Sub-Recipe</option>
            <option value="component">Component</option>
          </select>
        </div>
      </div>

      {/* Category & Cuisine */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">
            Category <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.category}
            onChange={(e) => updateField('category', e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select category...</option>
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Cuisine</label>
          <select
            value={formData.cuisine}
            onChange={(e) => updateField('cuisine', e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select cuisine...</option>
            {CUISINES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Times */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Prep Time (min)</label>
          <input
            type="number"
            min="0"
            value={formData.prepTime || ''}
            onChange={(e) => updateField('prepTime', parseInt(e.target.value) || 0)}
            placeholder="15"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Cook Time (min)</label>
          <input
            type="number"
            min="0"
            value={formData.cookTime || ''}
            onChange={(e) => updateField('cookTime', parseInt(e.target.value) || 0)}
            placeholder="30"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Yield</label>
          <input
            type="number"
            min="1"
            value={formData.yield || ''}
            onChange={(e) => updateField('yield', parseInt(e.target.value) || 1)}
            placeholder="4"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Yield Unit</label>
          <select
            value={formData.yieldUnit}
            onChange={(e) => updateField('yieldUnit', e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {YIELD_UNITS.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Difficulty */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Difficulty</label>
        <div className="flex gap-2">
          {DIFFICULTY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => updateField('difficulty', opt.value as RecipeFormData['difficulty'])}
              className={cn(
                'flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all',
                formData.difficulty === opt.value
                  ? opt.color + ' ring-2 ring-offset-2 ring-gray-400'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              <ChefHat className="h-4 w-4" />
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => updateField('description', e.target.value)}
          placeholder="Recipe description and notes..."
          rows={3}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Sub-Recipes */}
      {formData.type === 'finished' && (
        <div className="space-y-3">
          <label className="text-sm font-medium">Linked Sub-Recipes</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={subRecipeSearch}
              onChange={(e) => setSubRecipeSearch(e.target.value)}
              placeholder="Search sub-recipes to link..."
              className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          {subRecipeSearch && availableSubRecipes.length > 0 && (
            <div className="max-h-40 overflow-y-auto rounded-md border bg-background">
              {availableSubRecipes.slice(0, 5).map((sub) => (
                <button
                  key={sub.recipeId}
                  type="button"
                  onClick={() => {
                    toggleArrayItem('subRecipeIds', sub.recipeId);
                    setSubRecipeSearch('');
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted"
                >
                  <span>{sub.name}</span>
                  <span className="text-xs text-muted-foreground">{sub.type}</span>
                </button>
              ))}
            </div>
          )}
          {formData.subRecipeIds.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.subRecipeIds.map((id) => {
                const sub = allRecipes.find((r) => r.recipeId === id);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1 text-sm text-purple-800"
                  >
                    {sub?.name ?? id}
                    <button
                      type="button"
                      onClick={() => toggleArrayItem('subRecipeIds', id)}
                      className="ml-1 hover:text-purple-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

import React from 'react';
